/**
 * Settings store: in-memory cache + localStorage fallback + DB upsert.
 *
 * Mirrors the legacy `_settings` / `getSetting` / `setSetting` /
 * `loadAllSettings` from index.html. Reads are synchronous (cache → ls →
 * fallback). Writes are fire-and-forget against `user_settings` and never
 * block the caller.
 *
 * Safety:
 * - Sensitive keys (Zoho creds, Kipplo API key) flow through this module —
 *   never log values.
 * - DB writes require a session; if there is none yet, we still update
 *   cache + localStorage so the UI stays responsive.
 */

import { supabase } from './supabase'

/** Keys we eagerly migrate from localStorage on boot if the DB row is missing. */
const SEED_KEYS = [
  'kipplo_api_key',
  'kipplo_person_id',
  'kipplo_company_id',
  'kipplo_email_id',
  'kipplo_credit_limit',
  'kipplo_credits_used',
  'rot_days',
  'custom_stages',
  'saved_views',
  'goal_call',
  'goal_email',
  'goal_meeting',
  'zoho_account_id',
  'zoho_client_id',
  'zoho_client_secret',
  'zoho_refresh_token',
  'zoho_from_email',
  'zoho_from_name',
  'zoho_region',
] as const

const cache: Record<string, string> = {}

export function getSetting(key: string, fallback = ''): string {
  const cached = cache[key]
  if (cached !== undefined) return cached
  const ls = readLocal(key)
  return ls !== null ? ls : fallback
}

export async function setSetting(key: string, value: string): Promise<void> {
  cache[key] = value
  writeLocal(key, value)

  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return

  try {
    await supabase.from('user_settings').upsert(
      {
        user_id: user.id,
        setting_key: key,
        setting_value: value,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,setting_key' },
    )
  } catch (e) {
    console.warn('setSetting DB write failed for', key, e)
  }
}

/**
 * Loads all `user_settings` rows for the given user into the cache.
 * Falls back to localStorage seeds for SEED_KEYS not present in the DB.
 */
export async function loadAllSettings(userId: string): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('setting_key,setting_value')
      .eq('user_id', userId)
    if (!error && data) {
      for (const row of data as Array<{ setting_key: string; setting_value: string }>) {
        cache[row.setting_key] = row.setting_value
        writeLocal(row.setting_key, row.setting_value)
      }
    }
  } catch (e) {
    console.warn('loadAllSettings failed, using localStorage', e)
  }
  for (const k of SEED_KEYS) {
    if (cache[k] === undefined) {
      const ls = readLocal(k)
      if (ls !== null) cache[k] = ls
    }
  }
}

function readLocal(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocal(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value)
  } catch {
    // ignore quota / private mode errors
  }
}

/** Test-only helper: clears the in-memory cache. */
export function _resetSettingsForTests(): void {
  for (const key of Object.keys(cache)) delete cache[key]
}
